<script setup lang="ts">
import { useBillingStore, type SubscriptionSummary } from '~/store/billing'

const props = defineProps<{
  subscriptions: SubscriptionSummary[]
  loading?: boolean
  modelValue: 'new' | string
  pending?: boolean
}>()
const emit = defineEmits<{
  'update:modelValue': [value: 'new' | string]
  'update:pending': [value: boolean]
}>()

const billing = useBillingStore()
const labels = useBillingLabels()
// Prospective: the customer is choosing where to PUT a service, and a parked
// subscription renews on its cycle end once it holds one — so it keeps its date.
const { displayName: nameOf } = useSubscriptionDisplayName()
const displayName = (sub: Parameters<typeof nameOf>[0]) => nameOf(sub, { prospective: true })
const { billingLocale } = useRuntimeConfig().public

/**
 * "Existing" can be armed without a subscription chosen yet (the user clicked
 * the card but hasn't picked from the menu). modelValue stays 'new' until a
 * real id is emitted so the page never reprices against a half-made choice.
 */
const existingArmed = ref(props.modelValue !== 'new')
watch(
  () => props.modelValue,
  (v) => {
    if (v !== 'new') {
      existingArmed.value = true
      emit('update:pending', false)
    } else if (!existingArmed.value) {
      emit('update:pending', false)
    }
  }
)

const isExisting = computed(() => existingArmed.value)

function chooseNew() {
  existingArmed.value = false
  emit('update:modelValue', 'new')
  emit('update:pending', false)
}
function chooseExisting() {
  existingArmed.value = true
  emit('update:pending', props.modelValue === 'new')
}

const selectedId = computed<string | undefined>({
  get: () => (props.modelValue === 'new' ? undefined : props.modelValue),
  set: (v) => {
    emit('update:modelValue', v ?? 'new')
    emit('update:pending', !v)
  },
})

/** Named instances, with anything beyond the labelled ones collapsed to "+N". */
function instancesLine(sub: SubscriptionSummary): string {
  const { labels: names, instance_count } = sub.resources
  if (!names?.length) return ''
  const overflow = Math.max(0, instance_count - names.length)
  return overflow ? `${names.join(', ')} +${overflow}` : names.join(', ')
}

const fmtDay = (d: string) => new Date(d).toLocaleDateString(billingLocale)

/**
 * A subscription with no instances is "inactive" on the API — parked the hour
 * after its last instance was deleted, with the removal's temporary credit and
 * any prepaid balance still on it. It is offered here precisely so that money
 * can be spent; the badge says why it looks empty.
 */
const isParked = (sub: SubscriptionSummary) => sub.status === 'inactive'

/** Temporary credit that comes back to the customer only until the cycle ends. */
function temporaryCredit(sub: SubscriptionSummary): bigint {
  return sub.balance ? BigInt(sub.balance.temporary_micro_cents) : 0n
}

const items = computed(() =>
  [...props.subscriptions]
    .sort(
      (a, b) =>
        (b.resources?.instance_count ?? 0) - (a.resources?.instance_count ?? 0) ||
        displayName(a).localeCompare(displayName(b), billingLocale)
    )
    .map((sub) => ({
      label: displayName(sub),
      value: sub.id,
      // Searchable haystack: name, VM names and the id (for support tickets).
      search: [displayName(sub), sub.id, ...(sub.resources?.labels ?? [])]
        .join(' ')
        .toLowerCase(),
      sub,
    }))
)

const selectedSub = computed(
  () => props.subscriptions.find((s) => s.id === selectedId.value) ?? null
)
</script>

<template>
  <div v-if="loading" class="text-sm text-muted">
    {{ $t('billing.page.checkout.loadingSubscriptions') }}
  </div>
  <div
    v-else
    class="space-y-3"
    role="radiogroup"
    :aria-label="$t('billing.page.checkout.steps.billing')"
  >
    <!-- New subscription -->
    <button
      type="button"
      role="radio"
      :aria-checked="!isExisting"
      :class="[
        'w-full flex min-h-20 items-center gap-3 rounded-xl border-2 p-3.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]',
        !isExisting
          ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
          : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
      ]"
      @click="chooseNew"
    >
      <UIcon
        :name="!isExisting ? 'i-lucide-check-circle' : 'i-lucide-plus-circle'"
        :class="!isExisting ? 'text-primary size-5 shrink-0' : 'text-muted size-5 shrink-0'"
      />
      <div class="flex-1 min-w-0">
        <div class="font-medium text-sm">
          {{ $t('billing.page.checkout.picker.createNew') }}
        </div>
        <div class="mt-0.5 text-sm text-toned">
          {{ $t('billing.page.checkout.newSubscriptionDesc') }}
        </div>
      </div>
    </button>

    <!-- Add to an existing subscription (hidden when the client has none) -->
    <div
      v-if="subscriptions.length > 0"
      :class="[
        'w-full rounded-xl border-2 transition-colors',
        isExisting
          ? 'border-primary bg-primary/10 shadow-sm dark:bg-primary/15'
          : 'border-default bg-default hover:border-primary/60 hover:bg-elevated',
      ]"
    >
      <button
        type="button"
        role="radio"
        :aria-checked="isExisting"
        class="flex min-h-[4.75rem] w-full items-center gap-3 rounded-xl p-3.5 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ui-bg)]"
        @click="chooseExisting"
      >
        <UIcon
          :name="isExisting ? 'i-lucide-check-circle' : 'i-lucide-circle'"
          :class="isExisting ? 'text-primary size-5 shrink-0' : 'text-muted size-5 shrink-0'"
        />
        <div class="flex-1 min-w-0">
          <div class="font-medium text-sm">
            {{ $t('billing.page.checkout.picker.addToExisting') }}
          </div>
          <div class="mt-0.5 text-sm text-toned">
            {{ $t('billing.page.checkout.picker.addToExistingDesc') }}
          </div>
        </div>
      </button>

      <div v-if="isExisting" class="px-3 pb-3 space-y-2">
        <USelectMenu
          v-model="selectedId"
          :items="items"
          value-key="value"
          label-key="label"
          :filter-fields="['label', 'search']"
          :placeholder="$t('billing.page.checkout.picker.selectPlaceholder')"
          :search-input="{
            placeholder: $t('billing.page.checkout.picker.searchPlaceholder'),
            icon: 'i-lucide-search',
          }"
          class="w-full"
        >
          <template #default>
            <span v-if="selectedSub" class="flex items-center gap-2 min-w-0">
              <span class="font-medium truncate">{{ displayName(selectedSub) }}</span>
              <span class="text-xs text-muted whitespace-nowrap">
                {{
                  $t(
                    'billing.page.index.table.instanceCount',
                    { count: selectedSub.resources?.instance_count ?? 0 },
                    selectedSub.resources?.instance_count ?? 0
                  )
                }}
              </span>
            </span>
            <span v-else class="text-muted">
              {{ $t('billing.page.checkout.picker.selectPlaceholder') }}
            </span>
          </template>
          <template #item="{ item }">
            <div class="flex items-center justify-between gap-3 w-full py-0.5">
              <div class="min-w-0">
                <div class="flex items-center gap-2">
                  <span class="font-medium text-sm truncate">{{ item.label }}</span>
                  <UBadge
                    v-if="item.sub.term"
                    size="xs"
                    variant="subtle"
                    color="neutral"
                  >
                    {{ labels.termLabel[item.sub.term.kind] }}
                  </UBadge>
                  <UBadge
                    v-if="isParked(item.sub)"
                    size="xs"
                    variant="subtle"
                    color="warning"
                  >
                    {{ $t('billing.page.checkout.picker.noInstances') }}
                  </UBadge>
                </div>
                <div class="text-xs text-muted truncate">
                  {{
                    $t(
                      'billing.page.index.table.instanceCount',
                      { count: item.sub.resources?.instance_count ?? 0 },
                      item.sub.resources?.instance_count ?? 0
                    )
                  }}
                  <template v-if="item.sub.recurring">
                    · {{ billing.formatMicroCents(item.sub.recurring.monthly_micro_cents) }}{{ $t('billing.page.index.table.perMonth') }}
                  </template>
                  <template v-if="instancesLine(item.sub)">
                    · {{ instancesLine(item.sub) }}
                  </template>
                </div>
              </div>
              <div
                v-if="item.sub.balance"
                class="text-xs font-mono whitespace-nowrap"
                :class="
                  BigInt(item.sub.balance.usable_micro_cents) >= 0n
                    ? 'text-muted'
                    : 'text-error'
                "
              >
                {{ billing.formatMicroCents(item.sub.balance.usable_micro_cents) }}
              </div>
            </div>
          </template>
        </USelectMenu>

        <!-- Selected subscription at a glance -->
        <div
          v-if="selectedSub"
          class="flex items-center gap-4 flex-wrap text-xs text-muted"
        >
          <span>
            {{ $t('billing.page.checkout.available') }}
            <span
              class="font-medium"
              :class="
                selectedSub.balance &&
                BigInt(selectedSub.balance.usable_micro_cents) < 0n
                  ? 'text-error'
                  : 'text-default'
              "
            >
              {{
                selectedSub.balance
                  ? billing.formatMicroCents(selectedSub.balance.usable_micro_cents)
                  : '—'
              }}
            </span>
          </span>
          <span v-if="selectedSub.current_cycle_ends_at">
            {{ $t('billing.page.checkout.due') }}
            <span class="font-medium">{{ fmtDay(selectedSub.current_cycle_ends_at) }}</span>
          </span>
          <span v-if="instancesLine(selectedSub)" class="truncate">
            {{ instancesLine(selectedSub) }}
          </span>
        </div>

        <!-- Credit that only lasts until the cycle ends: say so before the
             customer decides how much to put on this subscription. -->
        <p
          v-if="selectedSub && temporaryCredit(selectedSub) > 0n && selectedSub.current_cycle_ends_at"
          class="flex items-start gap-1.5 text-xs text-toned"
        >
          <UIcon name="i-lucide-calendar-clock" class="size-3.5 mt-0.5 shrink-0 text-warning" />
          <span>
            {{
              $t('billing.page.checkout.picker.temporaryCredit', {
                amount: billing.formatMicroCents(selectedSub.balance!.temporary_micro_cents),
                date: fmtDay(selectedSub.current_cycle_ends_at),
              })
            }}
          </span>
        </p>
        <p
          v-if="selectedSub && isParked(selectedSub)"
          class="flex items-start gap-1.5 text-xs text-muted"
        >
          <UIcon name="i-lucide-info" class="size-3.5 mt-0.5 shrink-0" />
          <span>{{ $t('billing.page.checkout.picker.parkedHint') }}</span>
        </p>
      </div>
    </div>

    <p v-else class="text-xs text-muted pt-1">
      {{ $t('billing.page.checkout.noActiveSubscriptions') }}
    </p>
  </div>
</template>
